import React from "react";
import { parseISO, isValid, differenceInDays } from "date-fns";
import { STATUS_LABELS, STATUS_COLORS, getTargetGroupLabel } from "../../../constants/grants.constants";
import { getRegionLabel } from "../../../constants/regions";
import { formatDate } from "../../../utils/date.utils";
import { isSafeUrl } from "../../../utils/url-validation";

interface CallDetails {
  id: string;
  title: string;
  description: string | null;
  status: string;
  callType: string;
  grantType: string | null;
  applicationStartDate: string | null;
  applicationEndDate: string | null;
  announcedAt: string | null;
  fundingRange: {
    min?: number;
    max?: number;
    percentage?: number;
    currency: string;
  } | null;
  allocation: number | null;
  targetRegions: string[];
  targetGroups: string[];
  officialUrl: string | null;
  applicationUrl: string | null;
  program: {
    id: string;
    code: string;
    name: string;
    shortName: string;
  } | null;
  competition: {
    id: string;
    name: string;
    code: string | null;
    actionCode: string | null;
  } | null;
  operator: {
    id: string;
    name: string;
    shortName: string | null;
    type: string;
    website: string | null;
  } | null;
  project: {
    id: string;
    name: string;
    projectUrl: string | null;
    regions: string[];
  } | null;
}

interface CallDetailsCardProps {
  call: CallDetails;
}


const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return "-";
  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(1)} mln PLN`;
  }
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    currencyDisplay: "code",
  }).format(amount);
};

const getDaysLeft = (dateStr: string | null): number | null => {
  if (!dateStr) return null;
  try {
    const date = parseISO(dateStr);
    if (!isValid(date)) return null;
    return differenceInDays(date, new Date());
  } catch {
    return null;
  }
};

/**
 * Card component displaying detailed information about a grant call.
 */
const CallDetailsCard: React.FC<CallDetailsCardProps> = ({ call }) => {
  const daysLeft = getDaysLeft(call.applicationEndDate);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
      <div className="p-6">
        {/* Header with badges */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {call.program && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
              {call.program.code}
            </span>
          )}
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              STATUS_COLORS[call.status] || "bg-neutral-100 text-neutral-800"
            }`}
          >
            {STATUS_LABELS[call.status] || call.status}
          </span>
          {call.status === "OPEN" && daysLeft !== null && daysLeft >= 0 && (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                daysLeft < 3
                  ? "bg-red-100 text-red-800"
                  : daysLeft <= 7
                  ? "bg-orange-100 text-orange-800"
                  : "bg-neutral-100 text-neutral-600"
              }`}
            >
              {daysLeft === 0
                ? "Ostatni dzień!"
                : daysLeft === 1
                ? "Został 1 dzień"
                : `Zostało ${daysLeft} dni`}
            </span>
          )}
        </div>

        {/* Main info grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left column */}
          <div className="space-y-4">
            {/* Program */}
            {call.program && (
              <div>
                <div className="text-sm font-medium text-neutral-500 text-left">Program</div>
                <div className="mt-1 text-sm text-neutral-900 text-left">
                  {call.program.name}
                </div>
              </div>
            )}

            {/* Competition */}
            {call.competition && (
              <div>
                <div className="text-sm font-medium text-neutral-500 text-left">Konkurs</div>
                <div className="mt-1 text-sm text-neutral-900 text-left">
                  {call.competition.name}
                  {call.competition.actionCode && (
                    <span className="text-neutral-500 ml-2">
                      ({call.competition.actionCode})
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Operator Project */}
            {call.project && (
              <div>
                <div className="text-sm font-medium text-neutral-500 text-left">
                  Projekt operatora
                </div>
                <div className="mt-1 text-sm text-neutral-900 text-left">
                  {call.project.name}
                  {call.project.projectUrl &&
                    isSafeUrl(call.project.projectUrl) && (
                      <a
                        href={call.project.projectUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 text-primary-600 hover:text-primary-800"
                      >
                        (strona)
                      </a>
                    )}
                </div>
              </div>
            )}

            {/* Operator */}
            {call.operator && (
              <div>
                <div className="text-sm font-medium text-neutral-500 text-left">Operator</div>
                <div className="mt-1 text-sm text-neutral-900 text-left">
                  {call.operator.name}
                  {call.operator.website && isSafeUrl(call.operator.website) && (
                    <a
                      href={call.operator.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 text-primary-600 hover:text-primary-800"
                    >
                      (strona)
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Target Groups */}
            {call.targetGroups && call.targetGroups.length > 0 && (
              <div>
                <div className="text-sm font-medium text-neutral-500 text-left">
                  Grupy docelowe
                </div>
                <div className="mt-1 text-sm text-neutral-900 text-left">
                  {call.targetGroups.map(tg => getTargetGroupLabel(tg)).join(", ")}
                </div>
              </div>
            )}

            {/* Regions */}
            {call.targetRegions && call.targetRegions.length > 0 && (
              <div>
                <div className="text-sm font-medium text-neutral-500 text-left">Regiony</div>
                <div className="mt-1 text-sm text-neutral-900 text-left">
                  {call.targetRegions.map(r => getRegionLabel(r)).join(", ")}
                </div>
              </div>
            )}
          </div>

          {/* Right column */}
          <div className="space-y-4">
            {/* Dates */}
            <div>
              <div className="text-sm font-medium text-neutral-500 text-left">
                Termin składania wniosków
              </div>
              <div className="mt-1 text-sm text-neutral-900 text-left">
                od {formatDate(call.applicationStartDate)} do{" "}
                {formatDate(call.applicationEndDate)}
              </div>
            </div>

            {/* Allocation */}
            {call.allocation && (
              <div>
                <div className="text-sm font-medium text-neutral-500 text-left">Alokacja</div>
                <div className="mt-1 text-sm text-neutral-900 text-left">
                  {formatCurrency(call.allocation)}
                </div>
              </div>
            )}

            {/* Funding Range */}
            {call.fundingRange && (
              <div>
                <div className="text-sm font-medium text-neutral-500 text-left">
                  Dofinansowanie
                </div>
                <div className="mt-1 text-sm text-neutral-900 text-left">
                  {call.fundingRange.min !== undefined &&
                  call.fundingRange.max !== undefined ? (
                    <>
                      {formatCurrency(call.fundingRange.min)} -{" "}
                      {formatCurrency(call.fundingRange.max)}
                      {call.fundingRange.percentage && (
                        <span className="text-neutral-500 ml-1">
                          ({call.fundingRange.percentage}%)
                        </span>
                      )}
                    </>
                  ) : call.fundingRange.percentage ? (
                    `${call.fundingRange.percentage}%`
                  ) : (
                    "-"
                  )}
                </div>
              </div>
            )}

            {/* Description */}
            {call.description && (
              <div>
                <div className="text-sm font-medium text-neutral-500 text-left">Opis</div>
                <div className="mt-1 text-sm text-neutral-900 whitespace-pre-wrap text-left">
                  {call.description}
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default CallDetailsCard;
